import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { periodNoun } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import { canManageReport } from "@/profitability/access";
import type { RecordObject, RecordRef } from "@/profitability/records";
import type { RecordList } from "@/profitability/records/record-panel";
import { useFindings } from "@/profitability/review/query";
import { useReviewSpec } from "@/profitability/review/review-spec";
import { profitabilityTemplatePath } from "@/profitability/routes";
import { useEmptyActions } from "@/profitability/template-page/use-empty-actions";
import type { TemplatePeriod } from "@/profitability/template-page/use-template-period";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError, runDates } from "@/profitability/utils";
import { AskVolca } from "./ask-volca";
import { ChecksResult } from "./checks-result";
import { missingUpload } from "./missing-upload";
import { OverviewAttention } from "./overview-attention";
import { OverviewDetails } from "./overview-details";
import { INSIGHT_PREVIEW, OverviewInsights } from "./overview-insights";
import { OverviewPerformance } from "./overview-performance";
import {
  dashboardShape,
  OverviewSkeleton,
  DASHBOARD_COLUMNS,
} from "./overview-skeleton";
import { OverviewStart } from "./overview-start";
import { MarkReviewedButton } from "./period-review";

/** Performance first, then review, observations and the period's details. */
export const Overview = ({
  businessId,
  report,
  objects,
  onOpenRecord,
  onAsk,
  periodId = null,
  compare = null,
  period,
  recordsPending,
  chatOpen,
}: {
  businessId: string;
  report: ProfitabilityReport;
  objects: RecordObject[];
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
  /** Asks Volca in the chat beside the page. */
  onAsk: (question: string) => void;
  /** The run of the period to show; the latest when null. */
  periodId?: string | null;
  /** What the period is compared with; the period before when null. */
  compare?: string | null;
  /** The page's period, so the dashboard and the header share one request. */
  period: TemplatePeriod;
  /** Rows are still loading. Missing records are not an empty review. */
  recordsPending: boolean;
  chatOpen: boolean;
}) => {
  const spec = useReviewSpec();
  const { overview, review } = period;
  const runId = period.periodChanging ? "" : (period.run?.id ?? "");
  const findings = useFindings(
    businessId,
    report.id,
    "open",
    { runId },
    { enabled: !!runId && !!spec.work },
  );
  const actions = useEmptyActions(report, overview.data?.running);

  if (overview.isError)
    return (
      <ErrorState
        title="The latest report could not load"
        description={reportError(overview.error)}
        retrying={overview.isFetching}
      >
        <Button onClick={() => void overview.refetch()}>Try again</Button>
      </ErrorState>
    );
  if (overview.isPending || !overview.data) return <OverviewSkeleton />;
  if (period.periodChanging)
    return (
      <OverviewSkeleton
        shape={dashboardShape(overview.data, period.requestedId)}
      />
    );

  const { run, running, previous, figures, insights, history, checks } =
    overview.data;
  if (!run)
    return (
      <OverviewStart report={report} building={!!running} actions={actions} />
    );

  const dates = runDates(run);
  const work = objects.find((object) => object.dataset.id === spec.work);
  const periodWord = dates
    ? periodNoun(dayjs(dates.end_date).diff(dates.start_date, "day") + 1)
    : "period";
  const flagged = work
    ? {
        count: overview.data.flagged ?? 0,
        noun: work.name.toLowerCase(),
        plural: work.plural.toLowerCase(),
      }
    : null;
  const search = new URLSearchParams();
  if (periodId) search.set("period", periodId);
  if (compare) search.set("compare", compare);
  const query = search.size ? `?${search.toString()}` : "";
  const base = profitabilityTemplatePath(report.id);
  const insightsPath = `${base}/insights${query}`;
  const reviewPath = `${base}/review${query}`;
  const hasInsights = spec.sections.includes("insights") && insights.length > 0;
  const empty =
    checks && (overview.data.flagged ?? 0) === 0 ? (
      <ChecksResult
        checks={checks}
        noun={flagged?.noun ?? "record"}
        plural={flagged?.plural ?? "records"}
        period={periodWord}
        actions={actions}
        onAsk={onAsk}
      />
    ) : null;

  return (
    <Stack gap={8} className="@container w-full flex-1">
      <Stack
        className={cn("grid items-start gap-6", {
          [DASHBOARD_COLUMNS]:
            figures.length > 0 && (hasInsights || !!spec.work),
        })}
      >
        {figures.length > 0 && (
          <OverviewPerformance
            key={report.id}
            figures={figures}
            history={history}
            compared={!!previous?.comparable}
            insightsPath={insightsPath}
          />
        )}
        {(hasInsights || !!spec.work) && (
          <Stack gap={6} className="min-w-0">
            {hasInsights && (
              <Card size="sm">
                <CardContent>
                  <OverviewInsights
                    businessId={businessId}
                    insights={insights}
                    onAsk={(insight) => onAsk(`Why: ${insight.text}`)}
                    action={(insight) =>
                      missingUpload({
                        insight,
                        report,
                        objects,
                        run: { id: run.id, dates },
                      })
                    }
                    limit={INSIGHT_PREVIEW}
                    more={{ label: "View all", to: insightsPath }}
                  />
                </CardContent>
              </Card>
            )}
            {spec.work && (
              <OverviewAttention
                findings={period.findings}
                reviewing={period.findingsReview.reviewing}
                reviewPath={reviewPath}
                allPeriodsPath={periodId ? `${base}/review` : undefined}
                empty={empty}
                reviewAction={
                  canManageReport(report) && (
                    <MarkReviewedButton
                      review={review}
                      label={spec.labels.reviewed}
                      variant="ghost"
                    />
                  )
                }
              />
            )}
          </Stack>
        )}
      </Stack>
      <OverviewDetails
        overview={overview.data}
        objects={objects}
        counts={findings.data?.pages[0]?.counts}
        findingsError={findings.isError}
        review={review.state.data?.review ?? null}
        recordsPending={recordsPending}
        onOpenRecord={onOpenRecord}
      />
      <AskVolca
        key={run.id}
        placeholder="Ask Volca about this period…"
        onAsk={onAsk}
        hidden={chatOpen}
      />
    </Stack>
  );
};
